/**
 * Projects section read contracts — RO-3-10 / #44303.
 *
 * The in-process SSR reads the Projects pages called before the client port:
 *   projectsViews.bySlug  — project home + settings identity bootstrap
 *   projectsViews.catalog — `/projects?catalog=1` folders/health board
 *   projectsViews.map     — `/projects/map` workspace organization graph
 *
 * Writes stay where they already live under `projectActions.*` (RO-2-09b).
 * Live Next call sites stay on their server actions / in-process reads (D6).
 */

import { z } from "zod";
import type { Operation } from "../types";

// ── projectsViews.bySlug ────────────────────────────────────────────────────

const projectRow = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  source: z.string(),
  group_id: z.number().nullable(),
  workspace_id: z.number().nullable(),
  parent_id: z.number().nullable(),
  role: z.string().nullable(),
  claude_path: z.string().nullable(),
  claude_slug: z.string().nullable(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProjectBySlug = z.infer<typeof projectRow>;

const bySlugParams = z.object({ slug: z.string().min(1) });

const bySlugOp: Operation<
  z.infer<typeof bySlugParams>,
  void,
  void,
  ProjectBySlug
> = {
  method: "GET",
  path: ({ slug }) => `/api/projects/by-slug/${encodeURIComponent(slug)}`,
  params: bySlugParams,
  response: projectRow,
};

// ── projectsViews.catalog ───────────────────────────────────────────────────
// Port of `excellent/app/projects/page.tsx` board payload (when ?catalog=1).

const catalogProject = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  color: z.string().nullable(),
  icon: z.string().nullable(),
  source: z.string(),
  group_id: z.number().nullable(),
  parent_id: z.number().nullable(),
  sectionCount: z.number(),
  taskCount: z.number(),
  open: z.number(),
  review: z.number(),
  health: z.number().nullable().optional(),
  healthIntent: z
    .enum(["success", "warning", "danger", "neutral"])
    .optional(),
  trend: z.array(z.number()).optional(),
  links: z.number().optional(),
});
export type CatalogProject = z.infer<typeof catalogProject>;

const catalogResponse = z.object({
  workspaceName: z.string(),
  projects: z.array(catalogProject),
  folders: z.array(z.object({ id: z.number(), name: z.string() })),
  discovered: z.array(catalogProject),
  groupOptions: z.array(z.object({ id: z.number(), name: z.string() })),
  workspaceOptions: z.array(z.object({ id: z.number(), name: z.string() })),
  parentOptions: z.array(z.object({ id: z.number(), name: z.string() })),
});
export type ProjectsCatalogResponse = z.infer<typeof catalogResponse>;

const catalogOp: Operation<void, void, void, ProjectsCatalogResponse> = {
  method: "GET",
  path: () => "/api/projects/catalog",
  response: catalogResponse,
};

// ── projectsViews.map ───────────────────────────────────────────────────────
// Port of `excellent/app/projects/map/page.tsx` payload.

const mapProject = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  role: z.string().nullable(),
  group_id: z.number().nullable(),
  parent_id: z.number().nullable(),
  manual: z.boolean(),
});

const mapRelationship = z.object({
  id: z.number().optional(),
  from: z.string(),
  to: z.string(),
  kind: z.string().optional(),
}).passthrough();

const mapResponse = z.object({
  projects: z.array(mapProject),
  groups: z.array(z.object({ id: z.number(), name: z.string() })),
  relationships: z.array(mapRelationship),
  workspaces: z.array(z.object({ id: z.number(), name: z.string() })),
});
export type ProjectsMapResponse = z.infer<typeof mapResponse>;

const mapOp: Operation<void, void, void, ProjectsMapResponse> = {
  method: "GET",
  path: () => "/api/projects/map",
  response: mapResponse,
};

export const projectsViews = {
  bySlug: bySlugOp,
  catalog: catalogOp,
  map: mapOp,
};
